import { formatDicomDate, formatDicomTime } from '../overlay';
import type { DicomJson, SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { timedFetch } from '../net';

/**
 * Structured Report (SOP classes 1.2.840.10008.5.1.4.1.1.88.*): the content tree of the instance's
 * WADO-RS metadata rendered as plain DOM. Every value goes through textContent, never HTML, so report
 * text cannot inject markup.
 */

export const SR_TEXT = {
  loadError: ' No se ha podido cargar el informe estructurado desde el servidor. ',
} as const;

const TAG = {
  valueType: '0040A040',
  relationship: '0040A010',
  conceptName: '0040A043',
  conceptCode: '0040A168',
  content: '0040A730',
  text: '0040A160',
  measured: '0040A300',
  numeric: '0040A30A',
  units: '004008EA',
  date: '0040A121',
  time: '0040A122',
  dateTime: '0040A120',
  personName: '0040A123',
  uid: '0040A124',
  referencedSop: '00081199',
  referencedSopInstance: '00081155',
  codeMeaning: '00080104',
  codeValue: '00080100',
  completion: '0040A491',
  verification: '0040A493',
} as const;

/** Deepest content nesting rendered (guards against malformed, self-similar trees). */
const MAX_DEPTH = 32;

function items(json: DicomJson | undefined, tag: string): DicomJson[] {
  const value = json?.[tag]?.Value;
  return Array.isArray(value) ? (value.filter((v) => v && typeof v === 'object') as DicomJson[]) : [];
}

function first(json: DicomJson | undefined, tag: string): unknown {
  return json?.[tag]?.Value?.[0];
}

function text(json: DicomJson | undefined, tag: string): string {
  const value = first(json, tag);
  if (value === undefined || value === null) return '';
  if (typeof value === 'object' && 'Alphabetic' in (value as Record<string, unknown>)) {
    return String((value as { Alphabetic?: string }).Alphabetic ?? '').replace(/\^+/g, ' ').trim();
  }
  return String(value);
}

function codeMeaning(json: DicomJson | undefined, tag: string): string {
  const code = items(json, tag)[0];
  return code ? text(code, TAG.codeMeaning) || text(code, TAG.codeValue) : '';
}

function formatDateTime(value: string): string {
  return [formatDicomDate(value.slice(0, 8)), formatDicomTime(value.slice(8))].filter(Boolean).join(' ');
}

/** Human-readable value of one content item, by value type. */
export function contentValue(item: DicomJson): string {
  switch (text(item, TAG.valueType)) {
    case 'TEXT': return text(item, TAG.text);
    case 'NUM': {
      const measured = items(item, TAG.measured)[0];
      if (!measured) return '';
      const unit = codeMeaning(measured, TAG.units) || '';
      return [text(measured, TAG.numeric), unit].filter(Boolean).join(' ');
    }
    case 'CODE': return codeMeaning(item, TAG.conceptCode);
    case 'DATE': return formatDicomDate(text(item, TAG.date));
    case 'TIME': return formatDicomTime(text(item, TAG.time));
    case 'DATETIME': return formatDateTime(text(item, TAG.dateTime));
    case 'PNAME': return text(item, TAG.personName);
    case 'UIDREF': return text(item, TAG.uid);
    case 'COMPOSITE':
    case 'IMAGE':
    case 'WAVEFORM': return text(items(item, TAG.referencedSop)[0], TAG.referencedSopInstance);
    default: return '';
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, content?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (content !== undefined) node.textContent = content;
  return node;
}

function renderItems(parent: HTMLElement, list: DicomJson[], depth: number): void {
  if (list.length === 0 || depth > MAX_DEPTH) return;
  const ul = el('ul', 'sr-list');
  for (const item of list) {
    const valueType = text(item, TAG.valueType);
    const li = el('li', 'sr-item');
    li.dataset.valueType = valueType;
    const relationship = text(item, TAG.relationship);
    if (relationship) li.dataset.relationship = relationship;
    const name = codeMeaning(item, TAG.conceptName);
    if (valueType === 'CONTAINER') {
      li.appendChild(el('div', 'sr-heading', name));
    } else {
      if (name) li.appendChild(el('span', 'sr-name', `${name}: `));
      li.appendChild(el('span', 'sr-value', contentValue(item)));
    }
    renderItems(li, items(item, TAG.content), depth + 1);
    ul.appendChild(li);
  }
  parent.appendChild(ul);
}

/** The report's root dataset: the series metadata when it carries the content tree, else /metadata of the instance. */
async function reportDataset(shell: ViewerShell, series: SeriesRef): Promise<DicomJson> {
  const instance = series.instances[0];
  if (instance.metadata?.[TAG.content]) return instance.metadata;
  const response = await timedFetch(`${shell.ctx.client.instanceUrl(series, instance)}/metadata`, {
    headers: { Accept: 'application/dicom+json', ...shell.ctx.client.requestHeaders() },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`SR metadata ${response.status}`);
  const [json] = (await response.json()) as DicomJson[];
  if (!json) throw new Error('SR metadata empty');
  return json;
}

function showError(host: HTMLElement): void {
  host.replaceChildren(el('div', 'sr-error', SR_TEXT.loadError));
  host.dataset.sr = 'error';
}

/** Builds the report view in `host`; load failures show the IMEXHS error text in the cell. */
export async function renderSr(shell: ViewerShell, series: SeriesRef, host: HTMLDivElement): Promise<() => void> {
  let root: DicomJson;
  try {
    root = await reportDataset(shell, series);
  } catch (error) {
    console.warn('[viewer] structured report unavailable', error);
    showError(host);
    return () => undefined;
  }
  const content = items(root, TAG.content);
  if (content.length === 0) {
    showError(host);
    return () => undefined;
  }
  const doc = el('article', 'sr-document');
  doc.appendChild(el('h2', 'sr-title', codeMeaning(root, TAG.conceptName) || series.description));
  const flags = [text(root, TAG.completion), text(root, TAG.verification)].filter(Boolean).join(' · ');
  if (flags) doc.appendChild(el('div', 'sr-flags', flags));
  renderItems(doc, content, 0);
  host.appendChild(doc);
  host.dataset.sr = 'ready';
  // The report scrolls with the wheel; keep it from reaching the stack-scroll handlers underneath.
  const stopWheel = (event: WheelEvent) => event.stopPropagation();
  host.addEventListener('wheel', stopWheel, { passive: true });
  return () => host.removeEventListener('wheel', stopWheel);
}
